import { type MaterialSapVo } from "@/apis/sap.api";
import formStyles from "@/asserts/styles/formSection.module.less";
import { SelectMaterialForPurchaseModal } from "@/components/SelectMaterialModal/SelectMaterialForPurchaseModal";
import ModalSectionHeader from "@/components/ui/ModalSectionHeader";
import { Button, Form, Table, TableColumnType } from "antd";
import clsx from "clsx";
import { Trash2 } from "lucide-react";
import { useState } from "react";
import type { LowValuePurchaseRequisitionFormValue } from "../../type";
import styles from "./MaterialFormFields.module.less";
import { useMaterialCodeColumns } from "./hooks/useMaterialCodeColumns";
import { useFormEditable } from "../../hooks/useFormEditable";
import { useTranslation } from "react-i18next";

export const CodeMaterialFormFields = () => {
  const { t } = useTranslation("purchaseRequestManagement");
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const [materialModalOpen, setMaterialModalOpen] = useState(false);
  const form = Form.useFormInstance<LowValuePurchaseRequisitionFormValue>();

  const { isMainFormDisabled } = useFormEditable();

  const handleSelectMaterial = (materials: MaterialSapVo[]) => {
    let existingItems = form.getFieldValue("itemList") || [];

    existingItems = existingItems
      .filter(
        (item: any) =>
          !materials.some((m: any) => m.f101 === item.materialCode),
      )
      .filter((item: any) => item.materialCode);

    const newItems = materials.map((m) => ({
      materialCode: m.f101, // 物料编码
      materialName: m.f102 || m.f103, // 物料名称
      materialGroup: m.f105, // 物料组
      materialType: m.f104, // 物料类型
      unit: m.f107, // 单位
      setPerUnit: m.f111, // 每组单位
      lastPoUnitPrice: m.f112, // 上次PO单价/Last PO Unit Price
      yearBidPrice: m.f119, // 年采购均价
      lastPoSupplierCode: m.f117, // 最后PO供应商编码
      lastPoSupplierName: m.f118, // 最后PO供应商名称
      lastPoPriceUnit: m.f115, // 最后PO币种 (待补充)
      lastPoUnit: m.f116, // 上次PO价格数量单位
      lastPoQty: m.f114, // 最后PO数量
      closingStock: m.f110, // 现有库存
      safetyStock: m.f108, // 安全库存
    }));

    form.setFieldValue("itemList", [...existingItems, ...newItems]);

    setMaterialModalOpen(false);
  };

  const tableColumns = useMaterialCodeColumns();

  return (
    <>
      <div className={clsx(formStyles.formSection, styles.section)}>
        <ModalSectionHeader title={t("申请采购物品明细")} />

        <div className={styles.sectionBody}>
          <Form.List name="itemList">
            {(fields, { remove }) => {
              const indexColumn: TableColumnType = {
                title: t("序号"),
                dataIndex: "index",
                width: 80,
                align: "center",
                fixed: "left",
                render: (_, __, index) => {
                  const isHover = hoverIndex === index && !isMainFormDisabled;
                  return isHover ? (
                    <div
                      className={styles.removeIcon}
                      onClick={() => remove(index)}
                    >
                      <Trash2 size={20} />
                    </div>
                  ) : (
                    index + 1
                  );
                },
              };

              const columns = [indexColumn, ...tableColumns];

              return (
                <>
                  <div style={{ margin: "16px 0" }}>
                    <Button
                      type="primary"
                      onClick={() => setMaterialModalOpen(true)}
                    >
                      {t("获取物料")}
                    </Button>
                  </div>

                  <Table
                    bordered
                    dataSource={fields}
                    pagination={false}
                    scroll={{ x: 1600 }}
                    onRow={(_, index) => ({
                      onMouseEnter: () => setHoverIndex(index!),
                      onMouseLeave: () => setHoverIndex(null),
                    })}
                    columns={columns}
                    rowKey={(record) => record.key || record.name}
                  />
                </>
              );
            }}
          </Form.List>
        </div>
      </div>

      <SelectMaterialForPurchaseModal
        open={materialModalOpen}
        onOk={handleSelectMaterial}
        onClose={() => setMaterialModalOpen(false)}
      />
    </>
  );
};
